import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Select, Spin, Table, Tooltip, message } from "antd";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import { deleteSprServis, fetchSprServisList } from "./spr-servis-api";
import SprServisDrawer from "./spr-servis-drawer";
import {
  CDA_LABELS,
  VID_DIAGNOSIS,
  VID_DIARY,
  VID_OPTIONS,
  getLawFlag,
  normalizeServis
} from "./spr-servis-utils";
import "./spr-servis.css";

const DEFAULT_VID = VID_DIAGNOSIS;

const isAllowedVid = (value) => VID_OPTIONS.some((o) => o.value === value);

const filtersFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  const vidRaw = toNumber(src.get("vid"));
  const vid = isAllowedVid(vidRaw) ? vidRaw : DEFAULT_VID;
  return { vid };
};

const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const vid = isAllowedVid(filters?.vid) ? filters.vid : DEFAULT_VID;
  params.set("vid", String(vid));
  return params;
};

/**
 * Виды диагнозов и записей в дневник — SPR_SERVIS VID 41/42 (Delphi TFrmSprService).
 */
const SprServis = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const vid = urlFilters.vid;

  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 98) === 1;
  const canEdit = canView;

  const [items, setItems] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerIsNew, setDrawerIsNew] = useState(false);

  const listReqRef = useRef(0);
  const isDiary = vid === VID_DIARY;

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    return fetchSprServisList(vid)
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const rows = Array.isArray(data)
          ? data.map(normalizeServis).filter((r) => r.id != null)
          : [];
        setItems(rows);
        setSelectedId((prev) => {
          if (prev != null && rows.some((r) => r.id === prev)) return prev;
          return null;
        });
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить справочник");
        setItems([]);
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, [vid]);

  useEffect(() => {
    if (canView) loadList();
  }, [canView, loadList]);

  useEffect(() => {
    const next = filtersToSearchParams({ vid });
    if (searchParams.toString() === next.toString()) return;
    setSearchParams(next, { replace: true });
  }, [vid, searchParams, setSearchParams]);

  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [items, nameFilter]);

  const selectedItem = useMemo(
    () => items.find((t) => t.id === selectedId) || null,
    [items, selectedId]
  );

  const onVidChange = (nextVid) => {
    if (!isAllowedVid(nextVid) || nextVid === vid) return;
    setSelectedId(null);
    setNameFilter("");
    if (drawerOpen) setDrawerOpen(false);
    setSearchParams(filtersToSearchParams({ vid: nextVid }), { replace: true });
  };

  const openCreate = () => {
    if (!canEdit) return;
    setDrawerIsNew(true);
    setDrawerId(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.id == null) return;
    setSelectedId(row.id);
    setDrawerIsNew(false);
    setDrawerId(row.id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const onDrawerSaved = (saved) => {
    const id = toNumber(pick(saved, "id", "Id"));
    loadList().then(() => {
      if (id != null) {
        setSelectedId(id);
        setDrawerId(id);
        setDrawerIsNew(false);
      }
    });
  };

  const onDel = () => {
    if (!selectedItem || !canEdit) return;
    Modal.confirm({
      title: "Удалить?",
      content: `«${selectedItem.name || selectedItem.id}» будет удалено.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprServis(vid, selectedItem.id)
          .then(() => {
            message.success("Удалено");
            if (drawerId === selectedItem.id) closeDrawer();
            setSelectedId(null);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const columns = useMemo(() => {
    const cols = [
      {
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 64,
        align: "right"
      },
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        render: (name, row) => name || `#${row.id}`
      }
    ];
    if (isDiary) {
      cols.push(
        {
          title: "Разделы",
          dataIndex: "isOk",
          key: "isOk",
          width: 88,
          align: "center",
          render: (v) => (v === 1 ? <CheckOutlined /> : null)
        },
        {
          title: "Вид",
          dataIndex: "id1",
          key: "id1",
          width: 280,
          ellipsis: true,
          render: (id1) => CDA_LABELS[id1] || ""
        }
      );
    }
    return cols;
  }, [isDiary]);

  if (!canView) {
    return (
      <div className="spr-servis">
        <Alert
          type="warning"
          showIcon
          message="Нет права на просмотр справочника видов диагнозов и записей в дневник"
        />
      </div>
    );
  }

  return (
    <div className="spr-servis">
      {error ? (
        <Alert className="spr-servis__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-servis__body">
        <div className="spr-servis__list">
          <div className="spr-servis__toolbar">
            <div className="spr-servis__extra-actions">
              <Tooltip title="Добавить">
                <span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!canEdit}
                    aria-label="Добавить"
                    onClick={openCreate}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Изменить">
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedItem}
                    aria-label="Изменить"
                    onClick={() => openEdit(selectedItem)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Удалить">
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!canEdit || !selectedItem}
                    aria-label="Удалить"
                    onClick={onDel}
                  />
                </span>
              </Tooltip>
            </div>
            <Select
              className="spr-servis__vid"
              options={VID_OPTIONS}
              value={vid}
              onChange={onVidChange}
            />
            <Input
              allowClear
              variant="borderless"
              className="spr-servis__filter"
              placeholder="Фильтр по наименованию"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-servis__list-body">
            {loadingList && !items.length ? (
              <div className="spr-servis__center">
                <Spin />
              </div>
            ) : (
              <Table
                className="spr-servis__table"
                size="small"
                rowKey="id"
                pagination={false}
                columns={columns}
                dataSource={filteredItems}
                loading={loadingList}
                rowClassName={(row) =>
                  row.id === selectedId ? "spr-servis__row--selected" : ""
                }
                onRow={(row) => ({
                  onClick: () => setSelectedId(row.id),
                  onDoubleClick: () => openEdit(row)
                })}
                locale={{
                  emptyText: items.length ? "Ничего не найдено" : "Нет записей"
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SprServisDrawer
        open={drawerOpen}
        vid={vid}
        id={drawerId}
        isNew={drawerIsNew}
        canEdit={canEdit}
        onClose={closeDrawer}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default SprServis;
